import React, { useState, useContext, useEffect } from 'react';
import { Plus, X, Save, ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import activityService from '../../../../services/activityService';
import riskService from '../../../../services/riskService';

export default function AddActivity() {
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    activity_name: '',
    description: '',
    status: 'Active',
    selectedRisks: []
  });

  const [riskSearchQuery, setRiskSearchQuery] = useState('');
  const [filteredRisks, setFilteredRisks] = useState([]);
  const [showRiskDropdown, setShowRiskDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingRisks, setLoadingRisks] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRiskDropdown && !event.target.closest('.risk-search-container')) {
        setShowRiskDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRiskDropdown]);

  useEffect(() => {
    // Only search when query is at least 2 characters
    if (riskSearchQuery.length >= 2) {
      searchRisks(riskSearchQuery);
    } else {
      setFilteredRisks([]);
      setLoadingRisks(false);
    }
  }, [riskSearchQuery]);

  const searchRisks = async (query) => {
    setLoadingRisks(true);
    try {
      const result = await riskService.searchRisks(query, authTokens.access);
      if (result.success) {
        setFilteredRisks(result.data);
      } else {
        showError('Erreur lors de la recherche des risques');
        setFilteredRisks([]);
      }
    } catch (error) {
      console.error('Error searching risks:', error);
      showError('Erreur lors de la recherche des risques');
      setFilteredRisks([]);
    } finally {
      setLoadingRisks(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRiskSearch = (e) => {
    setRiskSearchQuery(e.target.value);
    setShowRiskDropdown(true);
  };

  const selectRisk = (risk) => {
    if (!formData.selectedRisks.find(r => r.id === risk.id)) {
      setFormData(prev => ({
        ...prev,
        selectedRisks: [...prev.selectedRisks, risk]
      }));
    }
    setRiskSearchQuery('');
    setShowRiskDropdown(false);
  };

  const removeRisk = (riskId) => {
    setFormData(prev => ({
      ...prev,
      selectedRisks: prev.selectedRisks.filter(r => r.id !== riskId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.activity_name.trim()) {
      showError('Le nom de l\'activité est requis');
      return;
    }

    if (formData.selectedRisks.length === 0) {
      showError('Veuillez sélectionner au moins un risque');
      return;
    }

    setIsSubmitting(true);
    try {
      const activityData = {
        activity_name: formData.activity_name,
        description: formData.description,
        status: formData.status,
        risks: formData.selectedRisks.map(risk => risk.id)
      };

      const result = await activityService.createActivity(activityData, authTokens.access);
      
      if (result.success) {
        showSuccess('Activité créée avec succès!');
        navigate('/dashboard/activities');
      } else {
        showError(result.error || 'Erreur lors de la création de l\'activité');
      }
    } catch (error) {
      console.error('Error creating activity:', error);
      showError('Erreur lors de la création de l\'activité');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProbabiliteBadge = (probabilite) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (probabilite?.toLowerCase()) {
      case 'critique': return `${baseClasses} bg-red-100 text-red-800`;
      case 'majeur': return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'normal': return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'mineur': return `${baseClasses} bg-blue-100 text-blue-800`;
      default: return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate('/dashboard/activities')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-2xl font-semibold text-gray-900">Ajouter une nouvelle activité</h2>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom de l'activité <span className="text-red-500">*</span>
            </label>
            <input type="text" value={formData.activity_name} onChange={(e) => handleInputChange('activity_name', e.target.value)} placeholder="Entrer le nom de l'activité..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} placeholder="Décrire l'activité..." rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
            <select value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500">
              <option value="Active">Actif</option>
              <option value="Inactive">Inactif</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risques associés <span className="text-red-500">*</span>
            </label>
            
            <div className="risk-search-container relative mb-4">
              <div className="relative">
                <input type="text" value={riskSearchQuery} onChange={handleRiskSearch} onFocus={() => setShowRiskDropdown(true)} placeholder="Rechercher un risque (min. 2 caractères)..." className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              
              {showRiskDropdown && riskSearchQuery.length >= 2 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {loadingRisks ? (
                    <div className="px-4 py-3 text-sm text-gray-500">Chargement...</div>
                  ) : filteredRisks.length > 0 ? (
                    filteredRisks.map((risk) => {
                      const isSelected = formData.selectedRisks.find(r => r.id === risk.id);
                      return (
                        <div key={risk.id} onClick={() => !isSelected && selectRisk(risk)} className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:bg-teal-50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0 mr-3">
                              <div className="font-medium text-gray-900">
                                {risk.risk_name}
                                {isSelected && <span className="ml-2 text-xs">(Sélectionné)</span>}
                              </div>
                              {risk.description && <div className="text-sm text-gray-500 truncate">{risk.description}</div>}
                            </div>
                            <span className={getProbabiliteBadge(risk.probability)}>{risk.probability}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">Aucun risque trouvé</div>
                  )}
                </div>
              )}
            </div>

            {formData.selectedRisks.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Risques sélectionnés ({formData.selectedRisks.length})</h4>
                {formData.selectedRisks.map((risk) => (
                  <div key={risk.id} className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">{risk.risk_name}</span>
                      <span className={getProbabiliteBadge(risk.probability)}>{risk.probability}</span>
                    </div>
                    <button type="button" onClick={() => removeRisk(risk.id)} className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors" title="Retirer">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Aucun risque sélectionné. Utilisez la recherche ci-dessus.</p>
            )}
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button type="button" onClick={() => navigate('/dashboard/activities')} className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" disabled={isSubmitting}>Annuler</button>
            <button type="submit" disabled={!formData.activity_name || formData.selectedRisks.length === 0 || isSubmitting} className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer l\'activité'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}